import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from './api';
import { logger } from '../lib/logger';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { user } = await authApi.me();
      setUser(user);
    } catch (err) {
      // 401 expected when not logged in; only log unexpected errors
      if (err?.status && err.status !== 401) {
        logger.error('Auth check failed:', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      logger.error('Logout failed:', err);
      // Continue regardless — we want to clear the user state locally
    }
    setUser(null);
    window.location.assign('/app/login');
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh: checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
